/**
 * 360° Turntable Spin Viewer
 * Vanilla JavaScript component for frame-based product rotation
 */

class Spin360Viewer {
  constructor(container) {
    this.container = container;
    this.viewport = container.querySelector('.spin-360-viewport');
    this.frameCounter = container.querySelector('.spin-360-frame-counter');
    this.autoplayToggle = container.querySelector('.spin-360-autoplay-toggle');
    
    // Parse configuration from data attributes
    this.config = {
      autoplay: container.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(container.dataset.autoplaySpeed) || 80,
      dragSensitivity: parseInt(container.dataset.dragSensitivity) || 15,
      reverse: container.dataset.reverse === 'true',
      loop: container.dataset.loop !== 'false' // Default true
    };

    // State
    this.frames = [];
    this.currentFrame = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragDistance = 0;
    this.autoplayInterval = null;
    this.autoplayActive = false;
    this.loaded = false;

    this.init();
  }

  init() {
    this.loadFrames();
    this.bindEvents();
    
    if (this.config.autoplay) {
      this.startAutoplay();
    }

    if (this.autoplayToggle) {
      this.updateAutoplayButton();
    }
  }

  loadFrames() {
    const images = this.viewport.querySelectorAll('img');
    
    if (images.length === 0) {
      console.warn('Spin360: No frames found');
      return;
    }

    this.frames = Array.from(images);
    let loadedCount = 0;

    // Preload all frames
    this.frames.forEach((img, index) => {
      if (img.complete) {
        loadedCount++;
      } else {
        img.addEventListener('load', () => {
          loadedCount++;
          if (loadedCount === this.frames.length) {
            this.onAllFramesLoaded();
          }
        });
      }
    });

    // Check if already loaded
    if (loadedCount === this.frames.length) {
      this.onAllFramesLoaded();
    }

    // Show first frame immediately
    this.showFrame(0);
  }

  onAllFramesLoaded() {
    this.loaded = true;
    this.viewport.classList.add('loaded');
    this.updateFrameCounter();
  }

  bindEvents() {
    // Mouse events
    this.viewport.addEventListener('mousedown', this.handleDragStart.bind(this));
    document.addEventListener('mousemove', this.handleDragMove.bind(this));
    document.addEventListener('mouseup', this.handleDragEnd.bind(this));

    // Touch events
    this.viewport.addEventListener('touchstart', this.handleDragStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.handleDragMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handleDragEnd.bind(this));

    // Autoplay toggle
    if (this.autoplayToggle) {
      this.autoplayToggle.addEventListener('click', this.toggleAutoplay.bind(this));
    }

    // Prevent context menu on viewport
    this.viewport.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  handleDragStart(e) {
    this.isDragging = true;
    this.viewport.classList.add('dragging');
    this.startX = this.getClientX(e);
    this.currentX = this.startX;
    this.dragDistance = 0;

    // Stop autoplay on interaction
    if (this.autoplayActive) {
      this.pauseAutoplay();
    }

    e.preventDefault();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    this.currentX = this.getClientX(e);
    const deltaX = this.currentX - this.startX;
    
    // Calculate frame change based on drag sensitivity
    const frameChange = Math.floor(Math.abs(deltaX) / this.config.dragSensitivity);
    
    if (frameChange > 0) {
      let direction = deltaX > 0 ? 1 : -1;
      
      // Apply reverse setting
      if (this.config.reverse) {
        direction *= -1;
      }

      const newFrame = this.currentFrame + (frameChange * direction);
      this.setFrame(newFrame);
      
      // Reset start position for continuous dragging
      this.startX = this.currentX;
    }
  }

  handleDragEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.viewport.classList.remove('dragging');
    this.dragDistance = 0;
  }

  getClientX(e) {
    return e.type.includes('touch') ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
  }

  setFrame(frameIndex) {
    if (this.frames.length === 0) return;

    // Handle looping or clamping
    if (this.config.loop) {
      // Loop infinitely
      frameIndex = ((frameIndex % this.frames.length) + this.frames.length) % this.frames.length;
    } else {
      // Clamp to bounds
      frameIndex = Math.max(0, Math.min(this.frames.length - 1, frameIndex));
    }

    if (frameIndex !== this.currentFrame) {
      this.currentFrame = frameIndex;
      this.showFrame(this.currentFrame);
      this.updateFrameCounter();
    }
  }

  showFrame(index) {
    this.frames.forEach((frame, i) => {
      if (i === index) {
        frame.classList.add('active');
      } else {
        frame.classList.remove('active');
      }
    });
  }

  updateFrameCounter() {
    if (this.frameCounter) {
      this.frameCounter.textContent = `${this.currentFrame + 1} / ${this.frames.length}`;
    }
  }

  startAutoplay() {
    if (this.autoplayInterval || this.frames.length === 0) return;

    this.autoplayActive = true;
    this.autoplayInterval = setInterval(() => {
      this.setFrame(this.currentFrame + 1);
    }, this.config.autoplaySpeed);

    this.updateAutoplayButton();
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    this.autoplayActive = false;
    this.updateAutoplayButton();
  }

  toggleAutoplay() {
    if (this.autoplayActive) {
      this.pauseAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  updateAutoplayButton() {
    if (!this.autoplayToggle) return;

    if (this.autoplayActive) {
      this.autoplayToggle.classList.add('active');
      this.autoplayToggle.textContent = 'Pause Spin';
    } else {
      this.autoplayToggle.classList.remove('active');
      this.autoplayToggle.textContent = 'Auto Spin';
    }
  }

  destroy() {
    this.pauseAutoplay();
    // Remove event listeners if needed
  }
}

// Auto-initialize all 360 spin viewers on page
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.spin-360-container');
  containers.forEach(container => {
    new Spin360Viewer(container);
  });
});

// Expose for programmatic initialization
window.Spin360Viewer = Spin360Viewer;
